import { useCallback, useEffect, useState } from "react";
import type { DfyEtaDto, Locale } from "../../shared/dto";
import { dfyEtaRangeMinutes } from "../DfyEtaLine";
import type {
  StaffMatrixClient,
  StaffQueueItem,
  StaffQueueResponse
} from "./model";
import { AutomationTimingEstimate } from "../AutomationTimingEstimate";
import { clockFields, intlLanguage } from "../date-format";
import { translateCount } from "../i18n";
import { StaffClientAvatar } from "./StaffRail";
import { BuildProgressRow } from "../BuildProgress";
import {
  STAFF_TEST_FILTER_STORAGE_KEY,
  readStoredStaffTestFilter,
  storeStaffTestFilter,
  visibleTestCount
} from "./test-filter";
import { ClientNameLine } from "./TestFilterNote";
import { staffOwnerLabel } from "./owner-label";
import { StaffUnassignedClients, type UnassignedClient } from "./UnassignedClients";
import type { StaffMember } from "../../shared/dto";
import type { StaffRun } from "./model";
import {
  clientHandleText,
  clientRowHandles,
  type ClientRowHandle
} from "./client-identity";

export interface StaffQueueStreams {
  mine: StaffQueueItem[];
  automation: StaffQueueItem[];
  waiting: StaffQueueItem[];
  risks: StaffQueueItem[];
}

export function partitionStaffQueue(items: StaffQueueItem[]): StaffQueueStreams {
  return {
    mine: items.filter((item) =>
      item.type === "review_publish" || item.type === "service_action"),
    automation: items.filter((item) =>
      item.type === "automation_watch" && !item.attention),
    waiting: items.filter((item) =>
      item.type === "client_blocked" && !item.attention),
    // step_blocked = a step the canonical truth hands back to the team with
    // missing outputs (fixr5, a3-01b); it always belongs among the risks.
    risks: items.filter((item) =>
      item.attention
      && (item.type === "client_blocked" || item.type === "step_blocked"
        || item.type === "automation_watch"))
  };
}

/**
 * The queue and the client list must describe the SAME world. The list hides
 * dev seeds behind one checkbox; without this filter the queue sent the
 * partner to clients that do not exist in their overview (audit P0-1).
 */
export function visibleQueueItems(
  items: StaffQueueItem[],
  hideTestTenants: boolean
): StaffQueueItem[] {
  return hideTestTenants ? items.filter((item) => !item.isTest) : items;
}

/**
 * What a failed load leaves on screen. A refresh that fails keeps the rows the
 * partner is working from and dates them; only a first load that never
 * produced anything may show the empty error state (audit2 N07).
 */
export function queueStateAfterFailure(
  current: "loading" | "ready" | "error"
): "ready" | "error" {
  return current === "ready" ? "ready" : "error";
}

/**
 * How to tell two rows apart when they point at different clients (F138 vada
 * 11). This used to be `ambiguousClientNames`, which added the address to
 * every row whose NAME another row shared - and stopped there, on the
 * assumption that an address is unique per person. Three clients in dev carry
 * two records each with the same name AND the same address, so the mark
 * printed the same string under both rows and the queue still showed the
 * reader two identical lines. The shared derivation names the record instead.
 */
export function queueClientHandles(items: StaffQueueItem[]): Map<string, ClientRowHandle> {
  return clientRowHandles(items.map((item) => ({
    clientId: item.clientId,
    name: item.clientName,
    email: item.clientEmail,
    createdAt: item.clientCreatedAt
  })));
}

export function StaffToday(props: {
  clients: StaffMatrixClient[];
  locale: Locale;
  t: (key: string) => string;
  onOpenClient: (clientId: string, step: string | null) => void;
  hideTestTenants?: boolean;
  onHideTestTenantsChange?: (hide: boolean) => void;
  initialQueue?: StaffQueueItem[];
  now?: Date;
  timeZone?: string;
  /** F139: the unassigned queue is an ADMIN surface and a mutation, so it
   *  needs the role and the console's own CSRF-carrying runner. */
  role?: "partner" | "admin";
  run?: StaffRun;
  initialUnassigned?: UnassignedClient[];
  initialPartners?: StaffMember[];
}) {
  const [items, setItems] = useState<StaffQueueItem[]>(props.initialQueue ?? []);
  const [state, setState] = useState<"loading" | "ready" | "error">(
    props.initialQueue ? "ready" : "loading"
  );
  const [refreshing, setRefreshing] = useState(false);
  // When the rows on screen were last true. A failed refresh keeps them and
  // says how old they are, so the partner can decide whether to trust them.
  const [loadedAt, setLoadedAt] = useState<Date | null>(
    props.initialQueue ? (props.now ?? new Date()) : null
  );
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [hideTestTenants, setHideTestTenants] = useState(() =>
    readStoredStaffTestFilter(props.hideTestTenants !== false));
  useEffect(() => {
    setHideTestTenants(props.hideTestTenants !== false);
  }, [props.hideTestTenants]);
  const timeZone = props.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const calls = staffCallsForDay(props.clients, props.now ?? new Date(), timeZone);
  const visible = visibleQueueItems(items, hideTestTenants);
  const streams = partitionStaffQueue(visible);
  const partnerNames = new Map(props.clients.map((client) => [client.id, client.partner?.name]));
  // Over the VISIBLE rows: a twin the test filter took away is not on screen,
  // so the survivor is unambiguous and wears no mark.
  const handles = queueClientHandles(visible);
  const hidden = items.length - visible.length;
  const shownTests = visibleTestCount(visible);

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    setState((current) => current === "ready" ? current : "loading");
    try {
      const response = await fetch("/api/staff/queue", { credentials: "include" });
      if (!response.ok) throw new Error("staff_queue_failed");
      const body = await response.json() as StaffQueueResponse;
      setItems(body.items);
      setLoadedAt(new Date());
      setRefreshFailed(false);
      setState("ready");
    } catch {
      // 🔴 A failed REFRESH must not delete the queue the partner is working
      // from. Two seconds without a signal used to replace twelve rows with an
      // empty apology (audit2 N07); the rows stay, a strip above them says how
      // old they are and offers the repeat. The empty error state belongs only
      // to a first load that never produced anything.
      setRefreshFailed(true);
      setState(queueStateAfterFailure);
    } finally {
      if (manual) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (props.initialQueue) return;
    void load();
    const interval = window.setInterval(() => void load(), 60_000);
    const onVisible = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load, props.initialQueue]);

  const total = streams.mine.length + calls.length
    + streams.automation.length + streams.waiting.length + streams.risks.length;

  return (
    <section className="staff-today" aria-labelledby="staff-today-title">
      <header className="staff-today-head">
        <div>
          <h1 id="staff-today-title">{props.t("partnerOS.today.title")}</h1>
          {state === "ready" && (
            <p className="staff-today-summary">
              {/* When the queue is empty the empty-state below already says
                  so; repeating it in the header said it twice. */}
              {total > 0
                ? props.t("partnerOS.today.summary").replace("{count}", String(total))
                : ""}
              {/* Whichever way the filter stands, the header describes the
                  rows underneath it: how many were taken away, or how many of
                  the ones on screen are dev seeds (audit P-12). */}
              {hidden > 0 && (
                <span className="staff-today-hidden">
                  {` · ${props.t("partnerOS.today.hiddenTest").replace("{count}", String(hidden))}`}
                </span>
              )}
              {hidden === 0 && shownTests > 0 && (
                <span className="staff-today-hidden">
                  {` · ${translateCount(props.locale, "partnerOS.today.shownTest", shownTests)}`}
                </span>
              )}
            </p>
          )}
        </div>
        <div className="staff-today-controls">
          <label className="checkbox-row" data-storage-key={STAFF_TEST_FILTER_STORAGE_KEY}>
            <input
              data-staff-test-filter
              type="checkbox"
              checked={hideTestTenants}
              onChange={(event) => {
                const next = event.target.checked;
                setHideTestTenants(next);
                storeStaffTestFilter(next);
                props.onHideTestTenantsChange?.(next);
              }}
            />
            <span>{props.t("staffConsole.hideTestTenants")}</span>
          </label>
          <button
            className="quiet-link"
            disabled={refreshing}
            onClick={() => void load(true)}
            type="button"
          >
            {refreshing ? props.t("common.loading") : props.t("partnerOS.today.refresh")}
          </button>
        </div>
      </header>
      {state === "loading" && <TodayLoading t={props.t} />}
      {state === "error" && (
        <div className="staff-today-state error" role="alert">
          <p>{props.t("partnerOS.today.error")}</p>
          <button className="secondary-button" onClick={() => void load(true)} type="button">
            {props.t("common.retry")}
          </button>
        </div>
      )}
      {state === "ready" && refreshFailed && loadedAt && (
        <div className="staff-today-stale" data-queue-stale role="status">
          <p>
            {/* F116: the tag and the clock fields come from the ONE mapping.
                A bare "en" plus `hour: "2-digit"` prints "09:05 AM" for a
                time every American writes as "9:05 AM". */}
            {props.t("partnerOS.today.refreshFailed").replace("{time}",
              new Intl.DateTimeFormat(intlLanguage(props.locale), {
                ...clockFields(props.locale), timeZone
              }).format(loadedAt))}
          </p>
          <button className="secondary-button compact-button" disabled={refreshing}
            onClick={() => void load(true)} type="button">
            {props.t("common.retry")}
          </button>
        </div>
      )}
      {/* F139: a client nobody owns is the ONE row on this screen that no
          partner can pick up, so it stands above the day. It renders
          independently of the queue's own load state - the two are separate
          reads, and a failed queue must not hide work that is still there. */}
      {props.role === "admin" && props.run && (
        <StaffUnassignedClients
          run={props.run}
          t={props.t}
          locale={props.locale}
          hideTestTenants={hideTestTenants}
          onOpenClient={props.onOpenClient}
          onHideTestTenantsChange={props.onHideTestTenantsChange}
          initialClients={props.initialUnassigned}
          initialPartners={props.initialPartners}
          timeZone={props.timeZone}
        />
      )}
      {state === "ready" && (
        <div className="staff-today-streams">
          <QueueStream
            role={props.role}
            partnerNames={partnerNames}
            handles={handles}
            items={streams.mine}
            name="mine"
            onOpenClient={props.onOpenClient}
            t={props.t}
            locale={props.locale}
          />
          {calls.length > 0 && (
            <section className="staff-today-stream staff-today-calls" data-today-stream="calls">
              <StreamHeading count={calls.length} name="calls" t={props.t} />
              <ol>
                {calls.map(({ client, call }) => (
                  <li key={call.id}>
                    <time dateTime={call.startsAt}>
                      {new Intl.DateTimeFormat(intlLanguage(props.locale), {
                        ...clockFields(props.locale),
                        timeZone
                      }).format(new Date(call.startsAt))}
                    </time>
                    <div>
                      <strong>{client.name}</strong>
                      <small>{props.t(`staffConsole.callKindValue.${call.kind}`)}</small>
                    </div>
                    <a className="secondary-button" href={call.zoomUrl} rel="noreferrer" target="_blank">
                      {props.t("partnerOS.today.joinCall")}
                    </a>
                  </li>
                ))}
              </ol>
            </section>
          )}
          {/* Order = urgency: my decisions, today's calls, things at risk,
              then the calm streams (running / waiting on the client). */}
          <QueueStream
            role={props.role}
            partnerNames={partnerNames}
            handles={handles}
            items={streams.risks}
            name="risks"
            onOpenClient={props.onOpenClient}
            t={props.t}
            locale={props.locale}
          />
          <QueueStream
            role={props.role}
            partnerNames={partnerNames}
            handles={handles}
            items={streams.automation}
            name="automation"
            onOpenClient={props.onOpenClient}
            t={props.t}
            locale={props.locale}
          />
          <QueueStream
            role={props.role}
            partnerNames={partnerNames}
            handles={handles}
            items={streams.waiting}
            name="waiting"
            onOpenClient={props.onOpenClient}
            t={props.t}
            locale={props.locale}
          />
          {total === 0 && (
            <div className="staff-today-state empty">
              <strong>{props.t("partnerOS.today.emptyTitle")}</strong>
              <p>{props.t("partnerOS.today.emptyBody")}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function staffCallsForDay(
  clients: StaffMatrixClient[],
  now: Date,
  timeZone: string
): Array<{ client: StaffMatrixClient; call: NonNullable<StaffMatrixClient["nextCall"]> }> {
  const today = localDateKey(now, timeZone);
  return clients
    .flatMap((client) => client.nextCall ? [{ client, call: client.nextCall }] : [])
    .filter((row) => localDateKey(new Date(row.call.startsAt), timeZone) === today)
    .sort((a, b) => Date.parse(a.call.startsAt) - Date.parse(b.call.startsAt));
}

function QueueStream(props: {
  role?: "partner" | "admin";
  partnerNames: Map<string, string | null | undefined>;
  items: StaffQueueItem[];
  name: keyof StaffQueueStreams;
  locale: Locale;
  t: (key: string) => string;
  handles: Map<string, ClientRowHandle>;
  onOpenClient: (clientId: string, step: string | null) => void;
}) {
  if (props.items.length === 0) return null;
  return (
    <section className={`staff-today-stream stream-${props.name}`} data-today-stream={props.name}>
      <StreamHeading count={props.items.length} name={props.name === "mine" && props.role === "admin" ? "team" : props.name} t={props.t} />
      <ol>
        {props.items.map((item) => {
          const handle = props.handles.get(item.clientId) ?? null;
          return (
          <li className={item.attention ? "attention" : undefined} key={item.id}>
            {/* The whole row is the target: one click, one destination. A
                column of identical accent buttons gave the eye nowhere to
                land (audit P1). */}
            <button
              className="today-queue-row"
              data-queue-action={item.type}
              onClick={() => props.onOpenClient(item.clientId, queueStep(item.deepLink))}
              type="button"
            >
              {/* The queue is about people: the row leads with the client's
                  face (initial), the owner survives as a badge on it. */}
              <span className={`today-queue-avatar owner-${item.owner}`} aria-hidden="true">
                <StaffClientAvatar email={item.clientEmail ?? ""} name={item.clientName} photoUrl={null} />
              </span>
              <span className="today-queue-copy">
                {/* A dev seed says so on its own row; the header count alone
                    left the partner one click from writing to a fixture. The
                    badge is a SIBLING of the name, so a long name cannot cut
                    it away (audit2 N01). */}
                <ClientNameLine isTest={item.isTest} name={item.clientName} t={props.t}>
                  {handle && (
                    <span className="today-queue-disambiguation" data-client-handle>
                      {clientHandleText(handle, props.t, props.locale)}
                    </span>
                  )}
                </ClientNameLine>
                <span className="today-queue-title">{item.title}</span>
                {props.role === "admin" && item.owner === "partner" && (
                  <small>{staffOwnerLabel(item.owner, props.role, props.partnerNames.get(item.clientId), props.t)}</small>
                )}
                {item.supersededByRun && (
                  <span className="today-queue-warning">
                    {props.t("partnerOS.today.newerBuilding")}
                  </span>
                )}
                {/* A live build used to be invisible here: the row carried only
                    `eta`, which during cold start is nothing, so the partner saw
                    no step, no count and no clock (Pavel 2026-08-05). */}
                {item.position && <BuildProgressRow position={item.position} t={props.t} />}
                <small className={item.ageMinutes >= 3 * 24 * 60 ? "is-stale" : undefined}>
                  {formatQueueAge(item.ageMinutes, props.locale)}
                  {item.eta && ` · ${etaLabel(item.eta, props.t)}`}
                  {item.alsoCount ? ` · ${props.t("partnerOS.today.alsoPhases")
                    .replace("{count}", String(item.alsoCount))}` : ""}
                </small>
                <AutomationTimingEstimate stats={item.automationStats} t={props.t} />
              </span>
              <span className="today-queue-go" aria-hidden="true">›</span>
            </button>
          </li>
          );
        })}
      </ol>
    </section>
  );
}

function StreamHeading(props: {
  count: number;
  name: string;
  t: (key: string) => string;
}) {
  return (
    <header>
      <h3>{props.t(`partnerOS.today.stream.${props.name}`)}</h3>
      <span>{props.count}</span>
    </header>
  );
}

function TodayLoading(props: { t: (key: string) => string }) {
  return (
    <div className="staff-today-loading" aria-label={props.t("common.loading")} role="status">
      <span />
      <span />
      <span />
    </div>
  );
}

function queueStep(deepLink: string): string | null {
  try {
    return new URL(deepLink, "https://launchpad.local").searchParams.get("staffStep");
  } catch {
    return null;
  }
}

function localDateKey(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone
  }).format(date);
}

/** The queue row says exactly what the client detail says: one estimate, one
 * band. A single-point "hotovo kolem 14:05" read more precise than four
 * validated runs can support. */
export function etaLabel(
  eta: DfyEtaDto,
  t: (key: string) => string,
  now = Date.now()
): string {
  if (!Number.isFinite(Date.parse(eta.etaAt)) || Date.parse(eta.etaAt) <= now) return "";
  const range = dfyEtaRangeMinutes(eta);
  return t("dfyEta.remainingRange")
    .replace("{from}", String(range.from))
    .replace("{to}", String(range.to));
}

function formatQueueAge(minutes: number, locale: Locale): string {
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: "always" });
  if (minutes < 60) return relative.format(-Math.max(1, Math.round(minutes)), "minute");
  if (minutes < 24 * 60) return relative.format(-Math.round(minutes / 60), "hour");
  return relative.format(-Math.round(minutes / (24 * 60)), "day");
}
